'use client';

import { type ReactNode, useState } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import { AccessGuard } from '@ps/features/access-control/components/access-guard/access-guard';
import type { BioAccessGuard } from '@ps/features/access-control/components/access-guard/guard-descriptor';
import { downgradeIfTierHeld } from '@ps/features/access-control/functions/downgradeIfTierHeld';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { creatorInfoFromStoreProfile } from '@ps/functions/bio/creatorInfo';
import type { ProfileSlim } from '@ps/functions/bio/profileSlim';
import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import {
  type PopStoreExtendedSubscriptionLevel,
  type PopStoreSubscriptionLevel,
} from '@ps/models';
import { type WithID } from '@ps/models/lib/base';
import { type Listing } from '@ps/models/lib/listing';
import { useCurrentUserId, useLogin } from '@ps/ui/contexts/auth';

import { useBioUserAccess } from '../../../bio/contexts/user-access/useBioUserAccess';
import { SubscribeDialog } from '../../../subscribe/components/SubscribeDialog';
import { AppUserPurchaseAlert } from '../../components/AppUserPurchaseAlert';
import { buildListingCheckoutUrl } from '../../functions/buildListingCheckoutUrl';
import { isPurchaseOwnershipResolved } from '../../functions/isPurchaseOwnershipResolved';
import {
  isDigitalListing,
  isExternalListing,
} from '../../functions/listingDisplay';
import type { ProductAccessGuard } from '../../functions/resolveProductAccessGuard';
import { usePurchasedDigitalAsset } from '../../hooks/usePurchasedDigitalAsset';
import { useVariantQuantity } from '../variant-quantity/context';
import { PdpPurchaseContext } from './context';

/**
 * Make a stored external link safe for `window.open`. Editors accept
 * protocol-less URLs (`pop.store/x`); without a scheme the browser resolves
 * them relative to the current origin, so default to `https://` when no
 * `http(s)` scheme is present.
 */
function ensureAbsoluteUrl(url: string): string {
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

type PdpPurchaseProviderProps = {
  listing: WithID<Listing>;
  /**
   * Bio context — builds the product return URL (login / post-checkout) AND
   * provides the seller/store id (`bioContext.creatorId`) used for the checkout
   * URL, the access gate, and the subscribe picker.
   */
  bioContext: BioContext;
  /**
   * Purchase/download access requirement (resolved server-side). When it isn't
   * `none`, the purchase tap is gated behind `AccessGuard` (follow / subscription
   * tier) before the checkout navigation runs. Owner-bypass + live follow /
   * subscription status are evaluated inside `AccessGuard`.
   */
  accessGuard?: ProductAccessGuard;
  /**
   * Seller profile (slim) — drives both the guard modals' identity (projected via
   * `creatorInfoFromStoreProfile`) and the in-place subscribe picker.
   */
  creatorProfile: ProfileSlim;
  children: ReactNode;
};

/**
 * Owns the PDP's single purchase/download flow and shares it — via
 * {@link PdpPurchaseContext} — with every CTA that triggers it (the inline
 * digital-summary button and the sticky-footer primary CTA). Mirrors RN, where
 * the summary row's `onDownload` and the primary button both call one
 * `onPurchase`.
 *
 * It wraps the whole `ProductDetail` so a SINGLE access-gate + subscribe / app
 * alert modal stack gates both buttons. Non-purchasable listings render no
 * context (the summary CTA stays inert, the sticky slot falls back to
 * `NonPurchasableActions`), matching the pre-existing behavior.
 *
 * For native physical/digital listings the flow soft-navigates to the shared
 * `/checkout` URL (via `buildListingCheckoutUrl`); external listings open
 * `externalCheckoutUrl`; inside the native app every path surfaces the IAP
 * alert. When `accessGuard` gates the listing, the tap is routed through
 * `AccessGuard` first (follow / subscription-upsell), mirroring RN's
 * view-then-gate on the download/buy action.
 */
export function PdpPurchaseProvider({
  listing,
  bioContext,
  accessGuard,
  creatorProfile,
  children,
}: PdpPurchaseProviderProps) {
  const ctx = useVariantQuantity();
  const router = useRouter();
  const login = useLogin();
  const currentUserId = useCurrentUserId();
  const isAppUser = useIsAppUser();
  // The subscribe picker opens IN PLACE (no navigation) for a subscription gate.
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  // Purchase inside the native app is blocked (IAP) — surface the alert instead.
  const [showAppAlert, setShowAppAlert] = useState(false);
  const {
    isFollowing,
    extendedSubscriptionLevel,
    loading: accessLoading,
    updateFollowingStatus,
    refreshFollowingStatus,
  } = useBioUserAccess();
  // A `?coupon=XXX` on the PDP URL is forwarded to the checkout link, where the
  // checkout auto-applies it (mirrors RN's URL coupon carried into purchase).
  const urlCoupon = useSearchParams().get('coupon') ?? undefined;
  const ownershipApplies = isDigitalListing(listing) && !isAppUser;
  const purchasedAsset = usePurchasedDigitalAsset(
    listing.id,
    ownershipApplies && currentUserId != null,
  );

  const nonPurchasable = Boolean(
    (listing as { nonPurchasable?: boolean }).nonPurchasable,
  );
  // No purchase action for a non-purchasable listing — render children without a
  // context so both CTAs read `null` (summary stays inert, the sticky slot shows
  // `NonPurchasableActions`).
  if (nonPurchasable) return <>{children}</>;

  if (purchasedAsset != null) {
    const openDownload = () => {
      window.open(purchasedAsset.downloadUrl, '_blank', 'noopener,noreferrer');
    };
    return (
      <PdpPurchaseContext.Provider
        value={{ onPurchase: openDownload, isReady: true, isPurchased: true }}
      >
        {children}
      </PdpPurchaseContext.Provider>
    );
  }
  const purchaseResolved = isPurchaseOwnershipResolved({
    ownershipApplies,
    currentUserId,
    purchasedAsset,
  });

  const external = isExternalListing(listing);
  const checkoutUrl = buildListingCheckoutUrl(listing, bioContext.urlCode, {
    variantId: ctx?.selectedVariant?.id,
    quantity: ctx?.quantity,
    coupon: urlCoupon,
  });

  const goToCheckout = () => {
    if (checkoutUrl) router.push(checkoutUrl);
  };

  const openExternal = () => {
    if (listing.externalCheckoutUrl) {
      window.open(
        ensureAbsoluteUrl(listing.externalCheckoutUrl),
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  // Gate applies only to native (non-external) listings that require follow /
  // subscription. External listings and ungated listings navigate directly.
  const gated = !external && accessGuard != null && accessGuard.kind !== 'none';

  if (!gated) {
    const onPurchase = () => {
      if (isAppUser) {
        setShowAppAlert(true);
        return;
      }
      if (external) {
        openExternal();
        return;
      }
      goToCheckout();
    };
    return (
      <PdpPurchaseContext.Provider
        value={{ onPurchase, isReady: purchaseResolved, isPurchased: false }}
      >
        {children}
        <AppUserPurchaseAlert
          open={showAppAlert}
          onClose={() => setShowAppAlert(false)}
        />
      </PdpPurchaseContext.Provider>
    );
  }

  // Return the buyer to THIS product after login and after the paid-tier
  // subscription checkout succeeds (the now-held tier satisfies the gate →
  // download unlocks). Built from primitives at render (never cached) — same
  // rule as the share permalink. Preserve the URL coupon so the discount still
  // applies on the follow-up purchase (RN's "coupon carried into purchase").
  const productPath = bioUrl(bioContext).product({
    id: listing.id,
    slug: listing.slug,
  });
  const productReturnUrl = urlCoupon
    ? `${productPath}?coupon=${encodeURIComponent(urlCoupon)}`
    : productPath;

  // Below-tier → open the subscribe picker IN PLACE (RN `defaultLevel`
  // preselected). Navigating to `/<urlCode>/subscribe` 404s from the legacy
  // `/products/...` route, so we mount the shared `SubscribeDialog` locally.
  // Subscribing needs an identity: a confirmed-anonymous viewer registers
  // "reopen the picker" as the post-login callback then is routed to a SOFT
  // `/login?next=<product>`; on success the login modal `router.back()`s and
  // fires the callback, reopening the picker.
  const goToSubscribe = () => {
    if (accessGuard?.kind !== 'subscription') return;
    // Auth loads lazily — `currentUserId` is `undefined` until it resolves.
    // Ignore a tap made before then, else a signed-in viewer would be misread as
    // logged-out and routed to /login. Only a resolved `null` (anonymous) logs in.
    if (currentUserId === undefined) return;
    if (currentUserId === null) {
      login({
        next: productReturnUrl,
        onSuccess: () => setSubscribeOpen(true),
      });
      return;
    }
    setSubscribeOpen(true);
  };

  // Map the PDP `ProductAccessGuard` → the shared `BioAccessGuard` descriptor.
  // `downgradeIfTierHeld` resolves the viewer-dependent tier check (a held tier
  // downgrades a `subscription` gate to `skip-follow`) in one shared place, so
  // the guard stays viewer-independent (mirrors `WiredCustomButtonLink`).
  const guard: BioAccessGuard = downgradeIfTierHeld(
    accessGuard?.kind === 'subscription'
      ? {
          kind: 'subscription',
          level: accessGuard.level as unknown as PopStoreSubscriptionLevel,
        }
      : { kind: 'follow' },
    extendedSubscriptionLevel,
  );

  return (
    <>
      <AccessGuard
        storeId={bioContext.creatorId}
        guard={guard}
        isFollowing={isFollowing}
        isSubscriptionResolved={!accessLoading}
        sellerProfile={creatorInfoFromStoreProfile(creatorProfile)}
        onFollowPersisted={() => {
          updateFollowingStatus(true);
          refreshFollowingStatus();
        }}
        onSubscriptionRequired={goToSubscribe}
        onAccessGranted={goToCheckout}
      >
        {(canProceed, requestAccess, isReady) => (
          <PdpPurchaseContext.Provider
            value={{
              onPurchase: () => {
                if (isAppUser) {
                  setShowAppAlert(true);
                  return;
                }
                if (canProceed) goToCheckout();
                else requestAccess();
              },
              isReady: isReady && purchaseResolved,
              isPurchased: false,
            }}
          >
            {children}
          </PdpPurchaseContext.Provider>
        )}
      </AccessGuard>

      {/* In-place subscribe picker for the subscription gate. Opens on the
          subscribe-confirm modal's "Subscribe". Picking a paid tier navigates to
          the community-subscription checkout; `softCheckoutNav` keeps this a soft
          SPA nav (the picker closes first so the checkout intercept overlays the
          product that stays mounted), and `successRedirectUrl` returns the buyer
          to this product on success so the held tier unlocks the download. */}
      <SubscribeDialog
        open={subscribeOpen}
        onOpenChange={setSubscribeOpen}
        profile={creatorProfile}
        defaultLevel={
          accessGuard?.kind === 'subscription'
            ? (accessGuard.level as unknown as PopStoreExtendedSubscriptionLevel)
            : undefined
        }
        successRedirectUrl={productReturnUrl}
        softCheckoutNav
      />

      <AppUserPurchaseAlert
        open={showAppAlert}
        onClose={() => setShowAppAlert(false)}
      />
    </>
  );
}
